import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, MoreVertical, Settings, Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axiosInstance from '@/api/axios';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [scribeDetails, setScribeDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    project: '',
    user: '',
    start_time: '',
    end_time: '',
    pre_charting_Hours: 0,
    pre_charting_mins: 0,
    prior_authorization: false,
  });

  useEffect(() => {
    fetchMastersOnly();
  }, []);

  const fetchMastersOnly = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([
        axiosInstance.get('/masters/projects'),
        axiosInstance.get('/masters/users'),
      ]);
      
      // Sort projects and users alphabetically
      const sortedProjects = projectsRes.data.sort((a, b) => 
        a.project_name.localeCompare(b.project_name)
      );
      const sortedUsers = usersRes.data.sort((a, b) => 
        a.user_name.localeCompare(b.user_name)
      );
      
      setProjects(sortedProjects);
      setUsers(sortedUsers);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchData = async (filterProject = '', filterUser = '') => {
    // Only fetch scribe details if BOTH project AND user are selected
    if (!filterProject || !filterUser) {
      setScribeDetails([]);
      return;
    }

    try {
      // Server-side filter + cache-buster so CDN/browser never returns stale data
      const scribeRes = await axiosInstance.get('/scribe', {
        params: {
          project: filterProject,
          user: filterUser,
          _: Date.now(),
        },
      });
      setScribeDetails(scribeRes.data);
    } catch (error) {
      toast.error('Failed to load scribe details');
    }
  };
  
  const calculateTimeDifference = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const checkExceedsTenHours = (detail) => {
    const start = new Date(detail.start_time);
    const end = new Date(detail.end_time);
    const durationMins = (end - start) / 60000;
    const preChartMins = detail.pre_charting_Hours * 60 + detail.pre_charting_mins;
    const totalMins = durationMins + preChartMins;
    return totalMins > 600; // 10 hours = 600 minutes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (new Date(formData.end_time) <= new Date(formData.start_time)) {
      toast.error('End time must be greater than start time');
      return;
    }

    // Calculate total hours (duration + pre-charting)
    const start = new Date(formData.start_time);
    const end = new Date(formData.end_time);
    const durationMins = (end - start) / 60000; // Convert to minutes
    const preChartMins = formData.pre_charting_Hours * 60 + formData.pre_charting_mins;
    const totalMins = durationMins + preChartMins;
    const totalHours = totalMins / 60;

    // Warning if total exceeds 10 hours
    if (totalHours > 10) {
      toast.warning(`Total time (${totalHours.toFixed(2)} hours) exceeds 10 hours. Entry will be saved but highlighted.`, {
        duration: 5000,
      });
    }

    setLoading(true);
    try {
      if (editingId) {
        await axiosInstance.put(`/scribe/${editingId}`, formData);
        toast.success('Scribe detail updated successfully');
        setEditingId(null);
      } else {
        await axiosInstance.post('/scribe', formData);
        toast.success('Scribe detail created successfully');
      }

      const keepProject = formData.project;
      const keepUser = formData.user;

      // Keep project and user, clear rest
      setFormData({
        project: keepProject,
        user: keepUser,
        start_time: '',
        end_time: '',
        pre_charting_Hours: 0,
        pre_charting_mins: 0,
        prior_authorization: false,
      });
      await fetchData(keepProject, keepUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scribe detail');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (detail) => {
    setEditingId(detail.scribeid);
    setFormData({
      project: detail.project,
      user: detail.user,
      start_time: new Date(detail.start_time).toISOString().slice(0, 16),
      end_time: new Date(detail.end_time).toISOString().slice(0, 16),
      pre_charting_Hours: detail.pre_charting_Hours,
      pre_charting_mins: detail.pre_charting_mins,
      prior_authorization: detail.prior_authorization,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axiosInstance.delete(`/scribe/${id}`);
      toast.success('Scribe detail deleted successfully');
      
      // Refresh the data with current filters
      if (formData.project && formData.user) {
        fetchData(formData.project, formData.user);
      }
    } catch (error) {
      toast.error('Failed to delete scribe detail');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axiosInstance.get('/scribe/export', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'scribe_details.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-medium tracking-tight">Scribe Dashboard</h1>
            <p className="text-xs text-muted-foreground mt-1">{user?.email}</p>
          </div>
          <div className="flex gap-3">
            {isAdmin() && (
              <>
                <Button
                  data-testid="reports-panel-button"
                  variant="ghost"
                  className="rounded-sm"
                  onClick={() => navigate('/reports')}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Reports Panel
                </Button>
                <Button
                  data-testid="admin-monthly-report-button"
                  variant="ghost"
                  className="rounded-sm"
                  onClick={() => navigate('/admin/monthly-report')}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Modify Scribe Data
                </Button>
                <Button
                  data-testid="admin-panel-button"
                  variant="ghost"
                  className="rounded-sm"
                  onClick={() => navigate('/admin')}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Panel
                </Button>
              </>
            )}
            <Button
              data-testid="logout-button"
              variant="ghost"
              className="rounded-sm"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Entry Form */}
        <div className="bg-white border border-border shadow-sm rounded-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-medium">{editingId ? 'Edit' : 'New'} Scribe Entry</h2>
            {editingId && (
              <Button
                variant="ghost"
                size="sm"
                className="rounded-sm"
                onClick={() => {
                  setEditingId(null);
                  // Keep project and user, clear rest
                  setFormData({
                    project: formData.project,
                    user: formData.user,
                    start_time: '',
                    end_time: '',
                    pre_charting_Hours: 0,
                    pre_charting_mins: 0,
                    prior_authorization: false,
                  });
                }}
              >
                Cancel Edit
              </Button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Project</Label>
              <Select 
                value={formData.project} 
                onValueChange={(value) => {
                  setFormData({ ...formData, project: value });
                  fetchData(value, formData.user);
                }} 
                required
              >
                <SelectTrigger data-testid="project-select" className="rounded-sm">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.project_name}>
                      {project.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">User</Label>
              <Select 
                value={formData.user} 
                onValueChange={(value) => {
                  setFormData({ ...formData, user: value });
                  fetchData(formData.project, value);
                }} 
                required
              >
                <SelectTrigger data-testid="user-select" className="rounded-sm">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.user_name}>
                      {user.user_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Start Time (India)</Label>
              <Input
                data-testid="start-time-input"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="rounded-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">End Time (India)</Label>
              <Input
                data-testid="end-time-input"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="rounded-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pre-charting Hours (0-23)</Label>
              <Input
                data-testid="pre-charting-hours-input"
                type="number"
                min="0"
                max="23"
                value={formData.pre_charting_Hours}
                onChange={(e) => setFormData({ ...formData, pre_charting_Hours: parseInt(e.target.value) || 0 })}
                className="rounded-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pre-charting Minutes (0-59)</Label>
              <Input
                data-testid="pre-charting-mins-input"
                type="number"
                min="0"
                max="59"
                value={formData.pre_charting_mins}
                onChange={(e) => setFormData({ ...formData, pre_charting_mins: parseInt(e.target.value) || 0 })}
                className="rounded-sm"
                required
              />
            </div>

            <div className="flex items-center space-x-2 md:col-span-2">
              <Checkbox
                data-testid="prior-authorization-checkbox"
                id="prior_authorization"
                checked={formData.prior_authorization}
                onCheckedChange={(checked) => setFormData({ ...formData, prior_authorization: checked })}
                className="rounded-sm"
              />
              <Label htmlFor="prior_authorization" className="text-sm cursor-pointer">
                Prior Authorization
              </Label>
            </div>

            <div className="md:col-span-2">
              <Button
                data-testid="submit-scribe-button"
                type="submit"
                className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90 shadow-sm transition-transform active:scale-95"
                disabled={loading}
              >
                {loading ? 'Saving...' : editingId ? 'Update Entry' : 'Submit Entry'}
              </Button>
            </div>
          </form>
        </div>

        {/* Scribe Details Table */}
        <div className="bg-white border border-border shadow-sm rounded-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-medium">Scribe Details</h2>
              {formData.project && formData.user && (
                <>
                  <Button
                    data-testid="clear-filters-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => {
                      setFormData({ ...formData, project: '', user: '' });
                      setScribeDetails([]);
                    }}
                  >
                    Clear Filters
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Showing entries for: <span className="font-medium">{formData.project}</span> - <span className="font-medium">{formData.user}</span>
                  </p>
                </>
              )}
              {(!formData.project || !formData.user) && (
                <p className="text-sm text-muted-foreground">
                  Select both Project and User to view scribe details
                </p>
              )}
            </div>
            <Button
              data-testid="export-excel-button"
              onClick={handleExport}
              className="rounded-sm bg-primary hover:bg-primary/90"
            >
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Project</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">User</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Start Time</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">End Time</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Duration</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Pre-chart</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Auth</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!formData.project || !formData.user ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Please select both Project and User above to view scribe details.
                    </TableCell>
                  </TableRow>
                ) : scribeDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No scribe details found for this Project and User combination.
                    </TableCell>
                  </TableRow>
                ) : (
                  scribeDetails.map((detail) => {
                    const duration = calculateTimeDifference(detail.start_time, detail.end_time);
                    const exceedsTenHours = checkExceedsTenHours(detail);
                    
                    return (
                      <TableRow 
                        key={detail.scribeid} 
                        className={`border-b hover:bg-muted/50 ${exceedsTenHours ? 'bg-yellow-50 border-l-4 border-l-yellow-500' : ''}`}
                        data-testid="scribe-detail-row"
                      >
                        <TableCell className="px-2 py-3 text-sm">{detail.project}</TableCell>
                        <TableCell className="px-2 py-3 text-sm">{detail.user}</TableCell>
                        <TableCell className="px-2 py-3 text-sm">
                          {new Date(detail.start_time).toLocaleString()}
                        </TableCell>
                        <TableCell className="px-2 py-3 text-sm">
                          {new Date(detail.end_time).toLocaleString()}
                        </TableCell>
                        <TableCell className="px-2 py-3 text-sm font-medium">
                          {duration}
                          {exceedsTenHours && (
                            <span className="ml-2 text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                              &gt;10hrs
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="px-2 py-3 text-sm">
                          {detail.pre_charting_Hours}h {detail.pre_charting_mins}m
                        </TableCell>
                        <TableCell className="px-2 py-3 text-sm">
                          {detail.prior_authorization ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell className="px-2 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="rounded-sm" data-testid="scribe-actions-menu">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-sm">
                              <DropdownMenuItem onClick={() => handleEdit(detail)} data-testid="edit-scribe-button">
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(detail.scribeid)}
                                className="text-destructive"
                                data-testid="delete-scribe-button"
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
